import { LngLatBounds, LngLat } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ReactNode, useMemo } from 'react';
import MapBox, { Marker, ViewState } from 'react-map-gl';

// Marker Icons
import { ReactComponent as CanMarkerJungle } from '../../assets/map-markers/can-marker-jungle.svg';
import { ReactComponent as CanMarkerLobster } from '../../assets/map-markers/can-marker-lobster.svg';
import { ReactComponent as LeafMarker } from '../../assets/map-markers/leaf-marker-default.svg';

type MarkerIcon = 'DEFAULT' | 'CAN_JUNGLE' | 'CAN_LOBSTER';

const pinIcons: Record<MarkerIcon, ReactNode> = {
  DEFAULT: <LeafMarker className="drop-shadow-light" />,
  CAN_JUNGLE: <CanMarkerJungle className="drop-shadow-light" />,
  CAN_LOBSTER: <CanMarkerLobster className="drop-shadow-light" />,
};

interface Location {
  long: number;
  lat: number;
  icon?: MarkerIcon;
}

export const getBoundingBox = (locations: Location[]) => {
  const longs = locations.map((l) => l.long);
  const lats = locations.map((l) => l.lat);

  const lowestLng = Math.min(...longs);
  const lowestLat = Math.min(...lats);

  const highestLng = Math.max(...longs);
  const highestLat = Math.max(...lats);

  return new LngLatBounds(new LngLat(lowestLng, lowestLat), new LngLat(highestLng, highestLat));
};

export interface MapProps {
  mapbox_token: string;
  locations?: Location[];
  interactive?: boolean;
  mapState?: Partial<ViewState>;
}

export const Map = ({ interactive = false, mapState = { zoom: 16 }, ...props }: MapProps) => {
  const { mapbox_token, locations } = props;

  // Memoize map markers
  const markers = useMemo(() => {
    return locations?.map((location, idx) => {
      return (
        <Marker key={idx} latitude={location.lat} longitude={location.long} anchor="center">
          {pinIcons[location.icon || 'DEFAULT']}
        </Marker>
      );
    });
  }, [locations]);

  const viewState = useMemo(() => {
    // Get bounding box if more than one location is provided
    if (locations && locations?.length > 1) {
      const bounds = getBoundingBox(locations);

      return {
        bounds,
        fitBoundsOptions: {
          padding: {
            top: 75,
            bottom: 30,
            left: 90,
            right: 90,
          },
        },
      } as Partial<ViewState>;
    } else {
      // Use the first location marker as the center of the map
      return {
        latitude: locations && locations[0].lat,
        longitude: locations && locations[0].long,
        ...mapState,
      } as Partial<ViewState>;
    }
  }, [mapState, locations]);

  return (
    <MapBox
      mapboxAccessToken={mapbox_token}
      initialViewState={viewState}
      style={{ width: '100%', height: '400px' }}
      interactive={interactive}
      mapStyle="mapbox://styles/mapbox/streets-v9"
      attributionControl={false}
      reuseMaps
      cooperativeGestures={true}
    >
      {markers}
    </MapBox>
  );
};

export default Map;
